const PATH_ID_ATTRIBUTE = 'data-path-id';

/**
 * Returns true when the element belongs to the interactive input layer overlay.
 */
const isElementWithinInputLayer = (element: Element, inputLayerNode?: HTMLElement | null): boolean => {
    if (!inputLayerNode) return false;
    return element === inputLayerNode || inputLayerNode.contains(element);
};

/**
 * Resolves the nearest path hit target that contains drawing metadata.
 */
const getPathHitElement = ({
    element,
    requiredClassName,
}: {
    element: Element;
    requiredClassName?: string;
}): HTMLElement | null => {
    const selector = requiredClassName ? `.${requiredClassName}[${PATH_ID_ATTRIBUTE}]` : `[${PATH_ID_ATTRIBUTE}]`;

    if (element.matches(selector)) return element as HTMLElement;
    return (element.closest(selector) as HTMLElement) || null;
};

/**
 * Finds the top-most hittable drawing path id from an elements stack, skipping overlay elements.
 */
export const getPathIdFromElementsStack = ({
    elements,
    inputLayerNode,
    requiredClassName,
}: {
    elements: Element[];
    inputLayerNode?: HTMLElement | null;
    requiredClassName?: string;
}): string | null => {
    for (const element of elements) {
        if (!element) continue;
        if (isElementWithinInputLayer(element, inputLayerNode)) continue;

        const pathHitElement = getPathHitElement({ element, requiredClassName });

        const pathId = pathHitElement?.dataset?.pathId;
        if (pathId) return pathId;
    }

    return null;
};

/**
 * Performs DOM hit testing at a client point and returns the top-most drawing path id.
 */
export const getPathIdFromDocumentHitTest = ({
    clientX,
    clientY,
    inputLayerNode,
    requiredClassName,
}: {
    clientX: number;
    clientY: number;
    inputLayerNode?: HTMLElement | null;
    requiredClassName?: string;
}): string | null => {
    const hitElements = document.elementsFromPoint ? document.elementsFromPoint(clientX, clientY) : [];

    return getPathIdFromElementsStack({
        elements: hitElements,
        inputLayerNode,
        requiredClassName,
    });
};
